import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { IDBPDatabase } from 'idb';

import {
  fetchGazeResultDetail,
  fetchGazeResults,
  finalizeChunkedUpload,
  normalizeSamplesForUpload,
  submitVideoSession,
  submitClientSummary,
  submitChunk,
  submitForm,
  uploadSamplesSequential,
} from '../api';
import type { VideoSubmitProgress } from '../api';
import { iterateKeys } from '../runner/iterateKeys';
import { deleteVideoArtifact, openOrCreateDB, readVideoArtifact, MyDB } from '../runner/openIDB';
import { computeMaxPayloadBytes, gzipPayload, toPositiveInt } from '../runner/uploadUtils';
import type { RoiSummaryV1 } from '../runner/roi';
import { useSession } from '../store/session';
import type { EyeTraxSample, TimelineSeg, IrisCollectionDraftV1 } from '../types';
import { clearClientRunId, getOrCreateClientRunId } from '../lib/clientRun';

interface TaskResult {
  task: string;
  label: string;
  confidence: number;
  prob?: number;
  threshold?: number;
}

interface DiagnosisResult {
  id: number;
  state?: string;
  label?: string;
  confidence?: number | null;
  tasks?: TaskResult[];
  name?: string;
}

interface UploadProgressInfo {
  sentChunks: number;
  totalChunks: number;
  bytes: number;
}

const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const DEBUG_ENABLED = process.env.NODE_ENV !== 'production';

const logDebug = (message: string, payload?: unknown) => {
  if (!DEBUG_ENABLED) return;
  if (payload !== undefined) {
    console.debug(message, payload);
  } else {
    console.debug(message);
  }
};

const FLUSH_BATCH_SIZE = toPositiveInt(import.meta.env.VITE_UPLOAD_BATCH_SIZE, 250);
const FLUSH_CONCURRENCY = Math.max(1, toPositiveInt(import.meta.env.VITE_UPLOAD_CONCURRENCY, 8));
const FLUSH_QUEUE_CAP = Math.max(FLUSH_CONCURRENCY * 2, FLUSH_CONCURRENCY + 1);
const FLUSH_BACKPRESSURE_MS = toPositiveInt(import.meta.env.VITE_UPLOAD_BACKPRESSURE_DELAY_MS, 20);
const MAX_PAYLOAD_BYTES = computeMaxPayloadBytes(import.meta.env.VITE_UPLOAD_MAX_BYTES);

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const readNumber = (key: string): number => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return 0;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  } catch (error) {
    logDebug(`[Done] failed to read numeric localStorage key ${key}`, error);
    return 0;
  }
};

const readStaticInfo = () => {
  try {
    const raw = localStorage.getItem('iris_static') ?? localStorage.getItem('argus_static');
    if (!raw) return undefined;
    if (!localStorage.getItem('iris_static') && localStorage.getItem('argus_static')) {
      localStorage.setItem('iris_static', raw);
      localStorage.removeItem('argus_static');
    }
    return JSON.parse(raw);
  } catch (error) {
    logDebug('[Done] failed to parse iris_static', error);
    return undefined;
  }
};

const readCollectionContext = (): IrisCollectionDraftV1 | undefined => {
  try {
    const raw = localStorage.getItem('iris_collection_context');
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as IrisCollectionDraftV1;
    if (parsed?.version !== 1) return undefined;
    return parsed;
  } catch (error) {
    logDebug('[Done] failed to parse iris_collection_context', error);
    return undefined;
  }
};

async function flushPendingIDB(
  formDataId: number,
  onFlushed?: (count: number) => void,
): Promise<number> {
  let db: IDBPDatabase<MyDB> | null = null;
  try {
    db = await openOrCreateDB();
  } catch (error) {
    logDebug('[Done] failed to open IndexedDB for flush', error);
    return 0;
  }

  if (!db) return 0;

  const queue: number[][] = [];
  const sleepers: Array<() => void> = [];
  const wake = () => {
    while (sleepers.length) {
      const resolver = sleepers.shift();
      if (resolver) resolver();
    }
  };

  let producerDone = false;
  let total = 0;

  const producer = (async () => {
    try {
      for await (const keys of iterateKeys(db!, formDataId, FLUSH_BATCH_SIZE)) {
        if (!keys.length) continue;
        while (queue.length >= FLUSH_QUEUE_CAP) {
          // eslint-disable-next-line no-await-in-loop
          await delay(FLUSH_BACKPRESSURE_MS);
        }
        queue.push(keys);
        wake();
      }
    } finally {
      producerDone = true;
      wake();
    }
  })();

  const worker = async () => {
    while (!producerDone || queue.length > 0) {
      const keys = queue.shift();
      if (!keys) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise<void>((resolve) => sleepers.push(resolve));
        continue;
      }

      try {
        const readTx = db!.transaction('landmarks', 'readonly');
        const store = readTx.objectStore('landmarks');
        const records = await Promise.all(keys.map((key) => store.get(key)));
        await readTx.done;

        const available: Array<{ key: number; record: MyDB['landmarks']['value'] }> = [];
        const missing: number[] = [];
        records.forEach((record, index) => {
          const key = keys[index];
          if (record) {
            available.push({ key, record });
          } else {
            missing.push(key);
          }
        });

        if (available.length > 0) {
          const uploadItems = available.map(({ key, record }) => ({
            key,
            sample: {
              timestamp: Number(record.timestamp),
              mediaIndex: Number(record.mediaIndex),
              landmarks: record.landmarks,
            } satisfies EyeTraxSample,
          }));

          const chunks: typeof uploadItems[] = [uploadItems];
          while (chunks.length) {
            const chunk = chunks.shift();
            if (!chunk || chunk.length === 0) continue;
            const seq = Math.max(0, Math.trunc(Number(chunk[0]?.key ?? 0)));
            const samples = chunk.map((item) => item.sample);
            const normalizedChunk = normalizeSamplesForUpload(samples);
            const preview = {
              formDataId,
              seq,
              final: false as const,
              samples: normalizedChunk,
            };
            const estimated = gzipPayload(preview).byteLength;
            if (estimated > MAX_PAYLOAD_BYTES && chunk.length > 1) {
              const mid = Math.ceil(chunk.length / 2);
              chunks.unshift(chunk.slice(mid));
              chunks.unshift(chunk.slice(0, mid));
              continue;
            }

            await submitChunk(formDataId, normalizedChunk, seq);
            total += chunk.length;
            onFlushed?.(chunk.length);
          }

          const deleteTx = db!.transaction('landmarks', 'readwrite');
          const deleteStore = deleteTx.objectStore('landmarks');
          await Promise.all(available.map(({ key }) => deleteStore.delete(key)));
          if (missing.length) {
            await Promise.all(missing.map((key) => deleteStore.delete(key)));
          }
          await deleteTx.done;
        } else if (missing.length) {
          const deleteTx = db!.transaction('landmarks', 'readwrite');
          const deleteStore = deleteTx.objectStore('landmarks');
          await Promise.all(missing.map((key) => deleteStore.delete(key)));
          await deleteTx.done;
        }
      } catch (error) {
        logDebug('[Done] flush worker failed', error);
        throw error;
      }
    }
  };

  try {
    await Promise.all([producer, ...Array.from({ length: FLUSH_CONCURRENCY }, worker)]);
  } finally {
    db.close();
  }

  return total;
}

const closeWindow = (navigate: ReturnType<typeof useNavigate>, result: DiagnosisResult | null) => {
  const targetPath = result?.id ? `/results/${result.id}` : '/dashboard';
  try {
    const channel = new BroadcastChannel('iris-runner');
    channel.postMessage({ type: 'iris:test:done', result });
    channel.close();
  } catch (error) {
    logDebug('[Done] broadcast channel failure', error);
  }

  try {
    localStorage.setItem('iris:test:done', Date.now().toString());
    if (result) {
      localStorage.setItem('iris:last_result', JSON.stringify(result));
    }
    localStorage.setItem('iris:please-close', Date.now().toString());
  } catch (error) {
    logDebug('[Done] failed to persist completion markers', error);
  }

  try {
    window.close();
    if (!window.closed) {
      window.open('', '_self');
      window.close();
    }
    logDebug('[Done] attempted window close', window.closed);
  } catch (error) {
    logDebug('[Done] window.close error', error);
  }

  window.setTimeout(() => {
    if (!window.closed) {
      logDebug('[Done] fallback navigate to result page', targetPath);
      navigate(targetPath, { replace: true });
    }
  }, 200);
};

export default function Done(): JSX.Element {
  const nav = useNavigate();
  const { samples, clear, timeline, videoBlob, videoMimeType, videoBytes } = useSession();
  const CAPTURE_MODE = String(import.meta.env.VITE_CAPTURE_MODE ?? 'roi').toLowerCase();
  const CLIENT_ROI_MODE =
    CAPTURE_MODE === 'roi' || CAPTURE_MODE === 'client' || CAPTURE_MODE === 'client-roi';

  const roiSummary = useMemo<RoiSummaryV1 | null>(() => {
    if (!CLIENT_ROI_MODE) return null;
    try {
      const raw =
        localStorage.getItem('iris_roi_summary') ?? localStorage.getItem('argus_roi_summary');
      if (!raw) return null;
      const parsed = JSON.parse(raw) as RoiSummaryV1;
      if (!parsed || parsed.version !== 1) return null;
      if (!localStorage.getItem('iris_roi_summary') && localStorage.getItem('argus_roi_summary')) {
        localStorage.setItem('iris_roi_summary', raw);
        localStorage.removeItem('argus_roi_summary');
      }
      return parsed;
    } catch (error) {
      logDebug('[Done] failed to parse iris_roi_summary', error);
      return null;
    }
  }, [CLIENT_ROI_MODE]);

  const ran = useRef(false);
  const isMountedRef = useRef(true);
  const latestKnownResultIdRef = useRef<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DiagnosisResult | null>(null);
  const [progress, setProgress] = useState<UploadProgressInfo | null>(null);
  const [videoProgress, setVideoProgress] = useState<VideoSubmitProgress | null>(null);
  const [recovering, setRecovering] = useState(false);
  const [recoveredVideo, setRecoveredVideo] = useState<{
    blob: Blob;
    mimeType: string | null;
    bytes: number;
  } | null>(null);

  const clientRunId = useMemo(() => getOrCreateClientRunId(), []);
  const persistedFormId = useMemo(() => {
    const irisId = readNumber('iris_formDataId');
    if (irisId) return irisId;
    const legacyId = readNumber('argus_formDataId');
    if (legacyId) {
      try {
        localStorage.setItem('iris_formDataId', String(legacyId));
        localStorage.removeItem('argus_formDataId');
      } catch {
        // ignore storage failures
      }
    }
    return legacyId;
  }, []);
  const staticInfo = useMemo(() => readStaticInfo(), []);
  const collectionContext = useMemo(() => readCollectionContext(), []);
  const effectiveVideoBlob = videoBlob ?? recoveredVideo?.blob ?? null;
  const effectiveVideoMimeType = videoMimeType ?? recoveredVideo?.mimeType ?? null;
  const effectiveVideoBytes =
    videoBytes > 0 ? videoBytes : recoveredVideo?.bytes ?? effectiveVideoBlob?.size ?? 0;
  const attachName = useCallback(
    (payload: DiagnosisResult): DiagnosisResult => {
      let rawName: unknown = undefined;
      if (collectionContext?.child?.name) {
        rawName = collectionContext.child.name;
      }
      if (staticInfo && typeof staticInfo === 'object') {
        const staticName = (staticInfo as Record<string, unknown>)['name'];
        if (typeof staticName === 'string' && staticName.trim().length > 0) {
          rawName = staticName;
        }
      }
      if (typeof rawName === 'string') {
        const trimmed = rawName.trim();
        if (trimmed.length > 0) {
          return {
            ...payload,
            name: trimmed,
          };
        }
      }
      return payload;
    },
    [collectionContext, staticInfo],
  );

  const clearCollectionStorage = useCallback(() => {
    try {
      localStorage.removeItem('iris_collection_context');
    } catch (error) {
      logDebug('[Done] failed to clear iris_collection_context', error);
    }
  }, []);

  const loadLatestResultId = useCallback(async (): Promise<number | null> => {
    try {
      const results = await fetchGazeResults();
      if (!Array.isArray(results) || results.length === 0) {
        latestKnownResultIdRef.current = null;
        return null;
      }
      const latest = results[0];
      const id = Number((latest as { id?: unknown }).id);
      const resolved = Number.isFinite(id) ? id : null;
      latestKnownResultIdRef.current = resolved;
      return resolved;
    } catch (loadError) {
      logDebug('[Done] failed to load latest result id', loadError);
      return latestKnownResultIdRef.current ?? null;
    }
  }, []);

  const isRecoverableError = (reason: unknown): boolean => {
    if (!(reason instanceof Error)) return false;
    const msg = reason.message || '';
    return [
      '499',
      'NetworkError',
      'Failed to fetch',
      'The network connection was lost',
      'Load failed',
    ].some((fragment) => msg.includes(fragment));
  };

  const pollForNewResult = useCallback(
    async (baselineId: number | null, startedAt: number): Promise<DiagnosisResult | null> => {
      const deadline = Date.now() + 90_000;
      while (Date.now() < deadline) {
        // eslint-disable-next-line no-await-in-loop
        await delay(4_000);
        try {
          const results = await fetchGazeResults();
          if (!Array.isArray(results) || results.length === 0) continue;
          const latest = results[0];
          const latestId = Number((latest as { id?: unknown }).id);
          if (!Number.isFinite(latestId)) continue;
          if (baselineId !== null && latestId <= baselineId) continue;

          const tsRaw = String((latest as { timestamp?: unknown }).timestamp ?? '');
          const ts = Date.parse(tsRaw);
          if (!Number.isNaN(ts) && ts + 5_000 < startedAt) {
            // Existing historical result; keep polling.
            continue;
          }

          const detail = await fetchGazeResultDetail(latestId);
          const tasks = Array.isArray(detail.tasks)
            ? (detail.tasks as TaskResult[]).map((task) => ({
                task: task.task,
                label: task.label,
                confidence: task.confidence,
                prob: task.prob,
                threshold: task.threshold,
              }))
            : undefined;

          latestKnownResultIdRef.current = latestId;
          return {
            id: latestId,
            label: detail.label,
            confidence: detail.confidence,
            tasks,
          };
        } catch (pollError) {
          logDebug('[Done] polling for finalized result failed', pollError);
        }
      }
      return null;
    },
    [],
  );

  const attemptRecovery = useCallback(
    async (
      reason: unknown,
      baselineId: number | null,
      startedAt: number,
    ): Promise<DiagnosisResult | null> => {
      if (!isRecoverableError(reason)) return null;
      if (isMountedRef.current) setRecovering(true);
      try {
        return await pollForNewResult(baselineId, startedAt);
      } finally {
        if (isMountedRef.current) setRecovering(false);
      }
    },
    [pollForNewResult],
  );

  const cleanupAfterFallback = useCallback(() => {
    if (persistedFormId > 0) {
      try {
        localStorage.removeItem('iris_formDataId');
        localStorage.removeItem('argus_formDataId');
      } catch (cleanupError) {
        logDebug('[Done] failed to remove persisted form id during cleanup', cleanupError);
      }
    }
    void deleteVideoArtifact(clientRunId).catch((cleanupError) =>
      logDebug('[Done] failed to delete recovered video artifact during cleanup', cleanupError),
    );
    clearClientRunId();
    clearCollectionStorage();
    clear();
  }, [clear, clearCollectionStorage, clientRunId, persistedFormId]);

  const hasVideoArtifact = Boolean(effectiveVideoBlob && effectiveVideoBytes > 0);

  const willFinalize = hasVideoArtifact
    ? true
    : CLIENT_ROI_MODE
      ? Boolean(roiSummary)
      : persistedFormId > 0 || samples.length > 0;
  const sampleCount = samples.length;

  logDebug('[Done] mount state', {
    willFinalize,
    CLIENT_ROI_MODE,
      persistedFormId,
      sampleCount,
      hasVideoArtifact,
      roiSummaryAvailable: Boolean(roiSummary),
    });

  const finalizeExisting = useCallback(async (): Promise<DiagnosisResult> => {
    if (persistedFormId <= 0) {
      throw new Error('No persisted form to finalize');
    }

    try {
      await flushPendingIDB(persistedFormId, (count) => {
        logDebug('[Done] flushed pending landmarks', count);
      });
    } catch (flushError) {
      logDebug('[Done] flushing pending landmarks failed', flushError);
    }

    const payload = { static: staticInfo, timeline: timeline as TimelineSeg[] };
    const response = await finalizeChunkedUpload(persistedFormId, payload);
    try {
      localStorage.removeItem('iris_formDataId');
      localStorage.removeItem('argus_formDataId');
    } catch (removeError) {
      logDebug('[Done] failed to remove persisted form id', removeError);
    }
    clearCollectionStorage();
    clear();
    clearClientRunId();
    return attachName(response);
  }, [attachName, clear, clearCollectionStorage, persistedFormId, staticInfo, timeline]);

  const uploadSequential = useCallback(async (): Promise<DiagnosisResult> => {
    logDebug('[Done] sequential upload path', samples.length);
    const { formDataId } = await submitForm('web-run');
    const payload = { static: staticInfo, timeline: timeline as TimelineSeg[] };
    const response = await uploadSamplesSequential(
      formDataId,
      samples as EyeTraxSample[],
      {
        targetMaxBytes: MAX_PAYLOAD_BYTES,
        seedChunkSize: 80,
        onProgress: setProgress,
      },
      payload,
    );
    clearCollectionStorage();
    clear();
    clearClientRunId();
    return attachName(response);
  }, [attachName, clear, clearCollectionStorage, samples, staticInfo, timeline]);

  const executeVideoSubmit = useCallback(async () => {
    setBusy(true);
    setError(null);
    setProgress(null);
    setVideoProgress(null);
    let baselineId: number | null = null;
    const startedAt = Date.now();
    try {
      if (!effectiveVideoBlob || effectiveVideoBytes <= 0) {
        throw new Error('未检测到完整视频，请重新开始采集');
      }
      if (!collectionContext) {
        throw new Error('缺少儿童与参考状态信息，请返回问卷页重新填写');
      }

      setVideoProgress({
        loadedBytes: 0,
        totalBytes: effectiveVideoBytes,
        percent: 0,
        phase: 'uploading',
      });
      baselineId = await loadLatestResultId();
      const response = await submitVideoSession(collectionContext, effectiveVideoBlob, {
        timeline: timeline as TimelineSeg[],
        roi: roiSummary,
        onProgress: (info) => {
          if (!isMountedRef.current) return;
          setVideoProgress(info);
        },
        meta: {
          mode: 'collect',
          client: 'projectiris',
          clientRunId,
          collectedAtClient: collectionContext.collectionMeta?.launchedAt,
          submittedAtClient: new Date().toISOString(),
          launcher: collectionContext.collectionMeta ?? null,
          videoMimeType: effectiveVideoMimeType,
          videoBytes: effectiveVideoBytes,
        },
      });
      if (!isMountedRef.current) return;

      try {
        localStorage.removeItem('iris_roi_summary');
        localStorage.removeItem('argus_roi_summary');
        localStorage.removeItem('iris_formDataId');
        localStorage.removeItem('argus_formDataId');
      } catch (cleanupError) {
        logDebug('[Done] failed to clear persisted upload markers after video submit', cleanupError);
      }
      await deleteVideoArtifact(clientRunId).catch((cleanupError) =>
        logDebug('[Done] failed to delete persisted video artifact after submit', cleanupError),
      );

      clearCollectionStorage();
      const attached = attachName({
        id: response.id,
        state: response.state,
        name: response.child?.name ?? undefined,
      });
      latestKnownResultIdRef.current = attached.id;
      setResult(attached);
      setBusy(false);
      setVideoProgress(null);
      clear();
      clearClientRunId();
      window.setTimeout(() => closeWindow(nav, attached), 200);
    } catch (submitError) {
      logDebug('[Done] video submit error', submitError);
      if (!isMountedRef.current) return;

      const recovered = await attemptRecovery(submitError, baselineId, startedAt);
      if (!isMountedRef.current) return;

      if (recovered) {
        cleanupAfterFallback();
        const attached = attachName(recovered);
        latestKnownResultIdRef.current = attached.id;
        setVideoProgress(null);
        setResult(attached);
        setBusy(false);
        window.setTimeout(() => closeWindow(nav, attached), 200);
        return;
      }

      setError(submitError instanceof Error ? submitError.message : '视频提交失败');
      setBusy(false);
      setVideoProgress(null);
    }
  }, [
    attachName,
    clear,
    cleanupAfterFallback,
    clearCollectionStorage,
    collectionContext,
    clientRunId,
    effectiveVideoBlob,
    effectiveVideoBytes,
    effectiveVideoMimeType,
    loadLatestResultId,
    nav,
    roiSummary,
    timeline,
    attemptRecovery,
  ]);

  const uploadAll = useCallback(async () => {
    if (persistedFormId > 0) {
      return finalizeExisting();
    }
    return uploadSequential();
  }, [finalizeExisting, persistedFormId, uploadSequential]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const executeUpload = useCallback(async () => {
    setBusy(true);
    setError(null);
    setProgress(null);
    const baselineId = await loadLatestResultId();
    const startedAt = Date.now();
    try {
      const response = await uploadAll();

      logDebug('[Done] uploadAll result', response);
      if (!isMountedRef.current) return;

      latestKnownResultIdRef.current = response.id;
      setResult(response);
      setBusy(false);
      window.setTimeout(() => closeWindow(nav, response), 200);
    } catch (uploadError) {
      logDebug('[Done] upload error', uploadError);
      if (!isMountedRef.current) return;

      const recovered = await attemptRecovery(uploadError, baselineId, startedAt);
      if (!isMountedRef.current) return;

      if (recovered) {
        cleanupAfterFallback();
        const attached = attachName(recovered);
        latestKnownResultIdRef.current = attached.id;
        setProgress(null);
        setResult(attached);
        setBusy(false);
        window.setTimeout(() => closeWindow(nav, attached), 200);
        return;
      }

      setError(uploadError instanceof Error ? uploadError.message : '上传失败');
      setBusy(false);
    }
  }, [attachName, attemptRecovery, cleanupAfterFallback, loadLatestResultId, nav, uploadAll]);

  const executeClientSubmit = useCallback(async () => {
    setBusy(true);
    setError(null);
    setProgress(null);

    try {
      const payload = {
        static: staticInfo,
        timeline: timeline as TimelineSeg[],
        roi: roiSummary,
        meta: { mode: 'collect', client: 'projectiris' },
      };
      const response = await submitClientSummary(payload);
      if (!isMountedRef.current) return;

      try {
        localStorage.removeItem('iris_roi_summary');
        localStorage.removeItem('argus_roi_summary');
      } catch (cleanupError) {
        logDebug('[Done] failed to remove iris_roi_summary', cleanupError);
      }

      const attached = attachName({
        ...response,
      });
      latestKnownResultIdRef.current = attached.id;
      setResult(attached);
      setBusy(false);
      clearCollectionStorage();
      clear();
      clearClientRunId();
      window.setTimeout(() => closeWindow(nav, attached), 200);
    } catch (submitError) {
      logDebug('[Done] client summary submit error', submitError);
      if (!isMountedRef.current) return;
      setError(submitError instanceof Error ? submitError.message : '提交失败');
      setBusy(false);
    }
  }, [attachName, clear, clearCollectionStorage, nav, roiSummary, staticInfo, timeline]);

  useEffect(() => {
    if (videoBlob && videoBytes > 0) return;
    let cancelled = false;
    readVideoArtifact(clientRunId)
      .then((artifact) => {
        if (cancelled || !artifact) return;
        setRecoveredVideo({
          blob: artifact.blob,
          mimeType: artifact.mimeType,
          bytes: artifact.bytes || artifact.blob.size,
        });
      })
      .catch((readError) => logDebug('[Done] failed to read persisted video artifact', readError));
    return () => {
      cancelled = true;
    };
  }, [clientRunId, videoBlob, videoBytes]);

  useEffect(() => {
    if (ran.current) return;

    if (!willFinalize) {
      ran.current = true;
      closeWindow(nav, null);
      return;
    }

    ran.current = true;
    if (hasVideoArtifact) {
      void executeVideoSubmit();
      return;
    }
    if (CLIENT_ROI_MODE) {
      void executeClientSubmit();
      return;
    }

    void executeUpload();
  }, [
    CLIENT_ROI_MODE,
    executeClientSubmit,
    executeUpload,
    executeVideoSubmit,
    hasVideoArtifact,
    nav,
    willFinalize,
  ]);

  return (
    <div className="full center stack" aria-label="Runner done">
      <Typography variant="h5" component="h2" sx={{ fontWeight: 800 }}>
        {error
          ? CLIENT_ROI_MODE
            ? '提交失败'
            : '上传失败'
          : result
            ? '采集已完成'
            : '正在提交数据'}
      </Typography>

      {error ? (
        <>
          <Typography component="p" color="error" sx={{ maxWidth: 520, textAlign: 'center' }}>
            {error}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center">
            <Button
              variant="outlined"
              onClick={() => {
                clear();
                nav('/runner/start');
              }}
            >
              重新开始
            </Button>
            {willFinalize && (
              <Button
                variant="contained"
                onClick={() => {
                  if (hasVideoArtifact) {
                    void executeVideoSubmit();
                  } else if (CLIENT_ROI_MODE) {
                    void executeClientSubmit();
                  } else {
                    void executeUpload();
                  }
                }}
              >
                {hasVideoArtifact ? '重试视频提交' : CLIENT_ROI_MODE ? '重试提交' : '重试上传'}
              </Button>
            )}
          </Stack>
        </>
      ) : result ? (
        <>
          <Alert severity="success" sx={{ width: 'min(520px, 100%)', textAlign: 'left' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              数据已保存
            </Typography>
            {result.name ? <Typography variant="body2">姓名：{result.name}</Typography> : null}
            <Typography variant="caption" sx={{ mt: 0.75, display: 'block', opacity: 0.85 }}>
              当前版本仅采集眼动数据，用于后续模型训练与验证；暂不输出结论。
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.85 }}>
              正在返回控制台…
            </Typography>
          </Alert>
        </>
      ) : (
        <>
          <Stack spacing={1.25} alignItems="center" sx={{ width: 'min(520px, 100%)' }}>
            <CircularProgress size={28} />
            <Typography component="p" sx={{ opacity: 0.85, textAlign: 'center' }}>
              {busy
                ? CLIENT_ROI_MODE
                  ? hasVideoArtifact
                    ? '正在上传完整视频与采集元数据，请保持窗口打开…'
                    : '正在提交数据，请保持窗口打开…'
                  : '正在上传数据并保存记录，请保持窗口打开…'
                : CLIENT_ROI_MODE
                  ? '正在准备提交…'
                  : '正在准备上传…'}
            </Typography>
            {busy ? (
              <Box sx={{ width: '100%', mt: 1 }}>
                <LinearProgress
                  variant={
                    hasVideoArtifact &&
                    videoProgress?.phase === 'uploading' &&
                    videoProgress.percent !== null
                      ? 'determinate'
                      : 'indeterminate'
                  }
                  value={
                    hasVideoArtifact && videoProgress?.phase === 'uploading'
                      ? Math.max(0, Math.min(100, videoProgress.percent ?? 0))
                      : undefined
                  }
                />
              </Box>
            ) : null}
            {busy && hasVideoArtifact && videoProgress ? (
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {videoProgress.phase === 'processing'
                  ? `已上传 ${formatBytes(videoProgress.totalBytes || effectiveVideoBytes)}，服务器正在写入记录…`
                  : `已上传 ${formatBytes(videoProgress.loadedBytes)} / ${formatBytes(videoProgress.totalBytes || effectiveVideoBytes)}${videoProgress.percent !== null ? ` (${Math.round(videoProgress.percent)}%)` : ''}`}
              </Typography>
            ) : null}
            {!CLIENT_ROI_MODE && progress ? (
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                上传进度{' '}
                {Math.round((progress.sentChunks / Math.max(1, progress.totalChunks)) * 100)}%
              </Typography>
            ) : null}
            <Typography variant="body2" sx={{ opacity: 0.7, textAlign: 'center' }}>
              {CLIENT_ROI_MODE ? '通常只需几秒即可完成。' : '最终结果生成可能需要 30–60 秒。'}
            </Typography>
            {recovering ? (
              <Typography variant="caption" sx={{ opacity: 0.85, textAlign: 'center' }}>
                正在确认服务器返回的结果，请勿关闭窗口…
              </Typography>
            ) : null}
          </Stack>
        </>
      )}
    </div>
  );
}
